import { buildQueryString } from '@msaf/core-common'
import { LookupDisplayValue, LookupResult, SearchQuery, SearchResult, SearchResultTokenResponse } from '../types/state'
import { SearchTemplate } from '../types/structure'

export type AuthStrategy = 'cookie' | 'none' | 'token'

export type BaseRequestOptions = {
  authStrategy: AuthStrategy
  beforeSend?: (input: RequestInfo, init: RequestInit) => RequestInit
  urlPrefix?: string
}

export type NoneAuthOptions = BaseRequestOptions & {
  authStrategy: 'none'
}

export type CookieAuthOptions = BaseRequestOptions & {
  authStrategy: 'cookie'
  csrfToken: string
}

export type TokenAuthOptions = BaseRequestOptions & {
  authStrategy: 'token'
  authToken: string
  authTokenScheme: string
}

export type RequestOptions = TokenAuthOptions | CookieAuthOptions | NoneAuthOptions

async function _makeRequest(input: RequestInfo, init?: RequestInit, options?: RequestOptions): Promise<Response> {
  let reqInit = init ?? {}

  // Set auth for request
  if (options?.authStrategy === 'token') {
    const tokenAutOptopns = options as TokenAuthOptions
    if (!tokenAutOptopns.authToken) {
      throw new Error('No auth token set for request to generic-search backend')
    }

    if (Array.isArray(reqInit.headers)) {
      throw new Error('Array type is not currently supported for generic search header init')
    }

    const bearer: string = `${tokenAutOptopns.authTokenScheme ?? 'Bearer'} ${tokenAutOptopns.authToken}`

    // Handle different ways `headers` object can exist in RequestInit
    if (!reqInit.headers) {
      reqInit.headers = {
        Authorization: bearer,
      }
    } else if ('Authorization' in reqInit.headers) {
      reqInit.headers['Authorization'] = bearer
    } else if (reqInit.headers instanceof Headers) {
      reqInit.headers.set('Authorization', bearer)
    } else {
      reqInit.headers['Authorization'] = bearer
    }
  }

  // Set URL for request
  if (typeof input !== 'string') {
    throw new Error('Request input that is not a string literal is not currently supported for generic search')
  }

  // De-dup slashes in request url
  const reqInput = `${options?.urlPrefix ?? '/api/generic-search'}/${input}`

  // Allow modification of request by caller prior to send.
  // Primarily used for custom authentication, but can be used to modify any aspect of the request
  if (options?.beforeSend) {
    reqInit = options.beforeSend(reqInput, reqInit)
  }

  return await fetch(reqInput, reqInit)
}

async function fetchApi<R = any>(input: RequestInfo, init?: RequestInit, options?: RequestOptions): Promise<R> {
  const response = await _makeRequest(input, init, options)
  if (!response.ok) {
    const cause = await response.json()
    const error = new Error(response.statusText, { cause })
    throw error
  }
  return response.json()
}

export function getSearchConfig(searchTypeKey: string, options?: RequestOptions) {
  return fetchApi<{ searchTemplate: SearchTemplate }>(`fetch-search-config/${searchTypeKey}`, undefined, options)
}

export function getSearchResult(searchQuery?: SearchQuery, options?: RequestOptions) {
  const data = new FormData()
  data.append('query', JSON.stringify({ searchQuery }))

  return fetchApi<{ searchResult: SearchResult }>(
    `fetch-search-results`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function getLookupResult(lookupQuery: Record<string, string | undefined>, options?: RequestOptions) {
  const data = new FormData()
  data.append('query', JSON.stringify({ lookupQuery }))

  return fetchApi<LookupResult>(
    `fetch-lookup-results`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function getLookupDisplayValue(lookupDisplayValueQuery: any, options?: RequestOptions) {
  const data = new FormData()
  data.append('query', JSON.stringify({ lookupDisplayValueQuery }))

  return fetchApi<LookupDisplayValue>(
    `fetch-lookup-display-value`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

/**
 * Fetch the coordinates for the selected address
 *
 * TODO: Move this to a new 'esri-lookup' package
 *
 * @param text Address text returned by the address search API
 * @param key Address key returned by the address search API
 * @param options Fetch request options
 * @returns
 */
export function getAddressCoordinates<R = any>(text: string, key: string, options?: RequestOptions) {
  const queryString = buildQueryString({ text, key })
  return fetchApi<R>(`esri-address/get-coordinates/?${queryString}`, undefined, options)
}

/**
 * Fetch address suggestions from the esri server
 *
 * TODO: Move this to a new 'esri-lookup' package
 *
 * @param query Entered by the user
 * @param options Fetch request options
 * @returns
 */
export function searchAddress<R = any>(query: string, options?: RequestOptions) {
  const queryString = buildQueryString({ q: query, includePlaces: true })
  return fetchApi<R>(`esri-address/search-physical/?${queryString}`, undefined, options)
}

export function getDetailedMapResult<R = any>(
  searchTypeKey: string,
  identifier: string | number | boolean | null,
  options?: RequestOptions,
) {
  const data = new FormData()
  data.append('query', JSON.stringify({ mapDetailQuery: { searchTypeKey, identifier } }))

  return fetchApi<R>(
    `fetch-detailed-map-result`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function getSearchResultExport(searchQuery?: SearchQuery, options?: RequestOptions) {
  const data = new FormData()
  data.append('query', JSON.stringify({ searchQuery }))

  return fetchApi<SearchResultTokenResponse>(
    `export-search-results`,
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function postSavedSearch<R = any>(
  searchQueryName: string,
  searchQueryJson: SearchQuery,
  options?: RequestOptions,
) {
  const data = new FormData()
  data.append('name', searchQueryName)
  data.append('query', JSON.stringify({ searchQuery: searchQueryJson }))

  return fetchApi<R>(
    'add-saved-query',
    {
      body: data,
      method: 'POST',
    },
    options,
  )
}

export function getSavedSearches<R = any>(searchTypeKey: string, options?: RequestOptions) {
  return fetchApi<R>(`fetch-saved-query-list/${searchTypeKey}`, undefined, options)
}

export function getSavedSearchResult<R = any>(id: number | undefined, options?: RequestOptions) {
  return fetchApi<R>(`fetch-saved-query/${id}`, undefined, options)
}
